import type { StyleSpecification } from "maplibre-gl";

// Atlas Studio grew out of an open-source editor that stored its data under
// its own name. This is the only place that name still appears: to read
// data written before the rename (browser storage, and keys inside styles
// saved locally or in the portal) and convert it.
const LEGACY_PREFIX = "maputnik:";
export const PREFIX = "atlas-studio:";

type Metadata = { [key: string]: unknown };

function upgradeKeys(metadata: Metadata | undefined): Metadata | undefined {
  if (!metadata || typeof metadata !== "object") return metadata;
  const legacy = Object.keys(metadata).filter((key) => key.startsWith(LEGACY_PREFIX));
  if (legacy.length === 0) return metadata;
  const next: Metadata = {};
  Object.entries(metadata).forEach(([key, value]) => {
    if (key.startsWith(LEGACY_PREFIX)) {
      const renamed = PREFIX + key.slice(LEGACY_PREFIX.length);
      // A value already stored under the new name is newer; it wins.
      if (!(renamed in metadata)) next[renamed] = value;
    } else {
      next[key] = value;
    }
  });
  return next;
}

// Renames old metadata keys of a style and of its layers. A style without
// any is returned as the same object.
export function upgradeLegacyMetadata<S extends StyleSpecification>(style: S): S {
  const metadata = upgradeKeys(style.metadata as Metadata | undefined);
  let layersChanged = false;
  const layers = (style.layers || []).map((layer) => {
    const before = (layer as { metadata?: Metadata }).metadata;
    const after = upgradeKeys(before);
    if (after === before) return layer;
    layersChanged = true;
    return { ...layer, metadata: after };
  });
  if (metadata === style.metadata && !layersChanged) return style;
  return { ...style, metadata, layers } as S;
}

type Storage = Pick<globalThis.Storage, "length" | "key" | "getItem" | "setItem" | "removeItem">;

// Moves browser storage written under the old name to the new keys, once,
// at start-up. Stored styles have their metadata upgraded on the way.
// Newer data under a new key is never overwritten.
export function migrateLegacyStorage(storage: Storage) {
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(LEGACY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => {
      const value = storage.getItem(key);
      const renamed = PREFIX + key.slice(LEGACY_PREFIX.length);
      if (value !== null && storage.getItem(renamed) === null) {
        storage.setItem(renamed, renamed.startsWith(`${PREFIX}style:`) ? upgradeStored(value) : value);
      }
      storage.removeItem(key);
    });
  } catch (err) {
    // Storage is blocked; nothing to move.
  }
}

function upgradeStored(json: string): string {
  try {
    return JSON.stringify(upgradeLegacyMetadata(JSON.parse(json)));
  } catch (err) {
    return json;
  }
}

// Token keys removed from a style before it leaves the editor (export),
// under both names, so an old saved style cannot leak a key either.
export function tokenKeys(source: string): string[] {
  return [`${PREFIX}${source}_access_token`, `${LEGACY_PREFIX}${source}_access_token`];
}
